import { NextPage } from 'next'
import Link from 'next/link'
import Image from 'next/legacy/image'
import { staticProps } from '../../utils/createApolloClient'
import { STORE_POPULAR_SONGS_QUERY } from '../../gql'

import StoreLayout from './components/StoreLayout'
import MostPopularSongs from './components/MostPopularSongs'
import classNames from 'classnames'
import StoreBanner from './components/StoreBanner'
import TryItBlock from './components/TryItBlock'

import mostPopularStyles from './components/_mostPopularSongs.module.scss'
import styles from './_index.module.scss'

import { t } from '../../utils'

import multitrackImg from '@public/images/store/multitrack.svg'
import patchesImg from '@public/images/store/patches.svg'
import vocalTracksImg from '@public/images/store/vocaltracks.svg'
import quoteAuthorImg from '@public/images/store/quoteauthor.png'
import heroDevicesImg from '@public/images/store/herodevices.png'
import { Song } from '../../__generated__/graphql'

interface MostPopularSongProps {
  storePopularSongs: Song[]
}

const StoreIndex: NextPage<MostPopularSongProps> & {
  theme?: string
} = ({ storePopularSongs }) => {
  return (
    <StoreLayout
      pageTitle={t('Store - Worship Online')}
      pageDescription={t(
        'From high-quality keyboard patches, to all the individual stems and multi-tracks (in every key), get the tools you need to practice and play!'
      )}
    >
      <StoreBanner></StoreBanner>
      <div className={classNames(styles.storeIndex, 'section')}>
        <div className="container">
          <MostPopularSongs songs={storePopularSongs} />
          <div
            className={classNames(
              'columns is-multiline is-mobile',
              styles.featuresBlock
            )}
          >
            <div className="column is-full-mobile is-6-desktop content">
              <h2>
                {t(
                  'See how easily you can enhance the sound of your worship set'
                )}
              </h2>
              <p>
                {t(
                  'With exclusive tracks, stems, and patches, the sound of your band will never suffer again.'
                )}
              </p>
              <p>
                {t(
                  'Whether you’re an individual musician, or a worship leader equipping your team, get instant access to resources that will have your band sounding better than the album this week.'
                )}
              </p>
              <div>
                <a className={mostPopularStyles.moreLink} href="/store">
                  {t('Learn more')} &gt;
                </a>
              </div>
            </div>
            <div className="column is-full-tablet is-6-desktop">
              <div className="columns is-mobile is-multiline is-centered">
                <div className="column is-full-mobile">
                  <Link
                    href="/store/products/premium_patches"
                    prefetch={false}
                    className={styles.featureLink}
                  >
                    <div className={styles.featureLinkImg}>
                      <Image
                        className={styles.headerDropdownImage}
                        src={patchesImg}
                        alt="Patches Icon"
                        width={92}
                        height={89}
                      />
                    </div>
                    <span>{t('Premium Patches')}</span>
                  </Link>
                </div>
                <div className="column is-full-mobile">
                  <Link
                    href="/store/products/multi_tracks"
                    prefetch={false}
                    className={styles.featureLink}
                  >
                    <div className={styles.featureLinkImg}>
                      <Image
                        className={styles.headerDropdownImage}
                        src={multitrackImg}
                        alt="Multi Tracks Icon"
                        width={92}
                        height={89}
                      />
                    </div>
                    <span>{t('Multi Tracks')}</span>
                  </Link>
                </div>
              </div>
              <div className="columns is-centered">
                <div className="column is-full-mobile">
                  <Link
                    href="/store/products/vocal_tracks"
                    prefetch={false}
                    className={styles.featureLink}
                  >
                    <div className={styles.featureLinkImg}>
                      <Image
                        className={styles.headerDropdownImage}
                        src={vocalTracksImg}
                        alt="Vocal Tracks Icon"
                        width={92}
                        height={89}
                      />
                    </div>
                    <span>{t('Vocal Tracks')}</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={classNames(styles.storeQuote, 'section')}>
        <div className="container">
          <div className="columns is-mobile is-centered">
            <div className="column is-9">
              <figure className={styles.quoteBlock}>
                <div className={styles.quoteImg}>
                  <Image
                    src={quoteAuthorImg}
                    alt={'DARREN E. THE BELONGING CO.'}
                  ></Image>
                </div>
                <blockquote>
                  Getting our tracks from Worship Online has taken our sets to
                  the next level. Vocalists and musicians can practice their
                  parts in any key. And we can run the tracks live so we have
                  all the extra synths and parts that match what our musicians
                  learned perfectly!
                </blockquote>
                <figcaption>
                  <span>DARREN E.</span> THE BELONGING CO.
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </div>
      <div className={classNames(styles.storeHeroDevices, 'section')}>
        <div className="container">
          <div className="columns is-centered is-mobile">
            <div className={classNames('column is-7', styles.heroDevices)}>
              <Image src={heroDevicesImg} alt={'Hero Devices'}></Image>
            </div>
          </div>
        </div>
      </div>
      <TryItBlock />
    </StoreLayout>
  )
}

export const getStaticProps = staticProps({
  additionalProps: async ({ client }) => {
    const {
      data: { storePopularSongs: storePopularSongs }
    } = await client.query({
      query: STORE_POPULAR_SONGS_QUERY
    })

    return {
      storePopularSongs
    }
  }
})
StoreIndex.theme = 'dark'
export default StoreIndex
